// @flow

import {
  CLEAR_STATUS_TEXT,
  LOGIN_USER_REQUEST,
  LOGIN_USER_SUCCESS,
  LOGIN_USER_FAILURE,
  LOGOUT_USER,
  UPDATE_MENTOR_STATUS,
  USER_IS_LOGGED_IN_FETCHING_ALL_DATA,
  USER_IS_LOGGED_IN_DATA_FETCHED,
  USER_IS_LOGGED_IN_REDIRECTED,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAILURE,
  CLEAR_RESET_PASSWORD,
  USER_NEEDS_TO_RESET_PASSWORD,
  UNLOCK_ACCOUNT_FAILURE,
  SET_THEME
} from '../actions/auth'

import { SET_LAST_SKILL_ASSESSMENT_AT } from '../actions/myquests'

import {
  UPDATE_ACCOUNT_REQUEST,
  UPDATE_ACCOUNT_SUCCESS,
  UPDATE_ACCOUNT_FAILURE,
  IS_EDITING_ACCOUNT,
  IS_NOT_EDITING_ACCOUNT
} from '../actions/account'

import { SET_USER_POLICIES_ACCEPTED } from '../actions/policies'

import { SET_CURRENT_ORGANIZATION } from '../actions/organization'

import {
  FETCH_USER_COURSES_SUCCESS,
  FETCH_PAST_USER_COURSES_SUCCESS
} from '../actions/usercourses'

import { GUARDIAN_STUDENT_SELECT, SET_GUARDIAN_USER } from '../actions/guardian'

const initialState: {
  token: ?string,
  userName: ?string,
  user: ?Object,
  userIsLoggedInFetchingAllData: boolean,
  isAuthenticated: boolean,
  isAuthenticating: boolean,
  isEditingAccount: boolean,
  isUpdatingAccount: boolean,
  isLoggedInAndRedirected: boolean,
  statusText: ?string,
  wasPasswordReset: ?boolean,
  userNeedsToResetPassword: boolean,
  userHasCurrentCourses: boolean,
  userHasPastCourses: boolean,
  unlockErrorStatusText: boolean,
  theme: string
} = {
  token: null,
  userName: null,
  user: {},
  userIsLoggedInFetchingAllData: false,
  isAuthenticated: false,
  isAuthenticating: false,
  isEditingAccount: false,
  isUpdatingAccount: false,
  isLoggedInAndRedirected: false,
  statusText: null,
  wasPasswordReset: null,
  userNeedsToResetPassword: false,
  userHasCurrentCourses: true, // more likely to be true
  userHasPastCourses: false,
  unlockErrorStatusText: null,
  theme: 'system'
}

export function auth(state: Object = initialState, action: Object): Object {
  switch (action.type) {
    case SET_USER_POLICIES_ACCEPTED: {
      const up = Object.assign({}, state.user)
      up.policies_acceptance = {
        '0': ''
      }
      return Object.assign({}, state, {
        user: up
      })
    }

    case CLEAR_STATUS_TEXT: {
      return Object.assign({}, state, {
        statusText: null
      })
    }

    case USER_IS_LOGGED_IN_FETCHING_ALL_DATA: {
      return Object.assign({}, state, {
        userIsLoggedInFetchingAllData: true
      })
    }

    case USER_IS_LOGGED_IN_DATA_FETCHED: {
      return Object.assign({}, state, {
        userIsLoggedInFetchingAllData: false
      })
    }

    case USER_IS_LOGGED_IN_REDIRECTED: {
      return Object.assign({}, state, {
        isLoggedInAndRedirected: true
      })
    }

    case LOGIN_USER_REQUEST: {
      return Object.assign({}, state, {
        isAuthenticating: true,
        statusText: null
      })
    }

    case UPDATE_MENTOR_STATUS: {
      return Object.assign({}, state, {
        isMentor: action.payload
      })
    }

    case LOGIN_USER_SUCCESS: {
      return Object.assign({}, state, {
        isAuthenticating: false,
        isAuthenticated: true,
        token: action.user.token,
        userName: action.user.display_name,
        user: action.user,
        isMentor: action.isMentor
      })
    }

    case LOGIN_USER_FAILURE: {
      return Object.assign({}, state, {
        isAuthenticating: false,
        isAuthenticated: false,
        isLoggedInAndRedirected: false,
        token: null,
        userName: null,
        statusText: action.payload.statusText
      })
    }

    case LOGOUT_USER: {
      return Object.assign({}, state, {
        isAuthenticated: false,
        isAuthenticating: false,
        isLoggedInAndRedirected: false,
        userIsLoggedInFetchingAllData: false,
        token: null,
        userName: null,
        isMentor: null,
        user: null,
        statusText: null
      })
    }

    case SET_GUARDIAN_USER: {
      const userInitialState = state.user
      return Object.assign({}, state, {
        user: {
          ...userInitialState,
          guardianUserData: {
            name: action.payload.name,
            token: action.payload.token,
            id: action.payload.id,
            email: action.payload.email
          }
        }
      })
    }

    // when guardian selects a student, populate auth.user with the student's information
    case GUARDIAN_STUDENT_SELECT: {
      const orgs = state.user.organizations[0]
      const userInitialState = state.user
      return Object.assign({}, state, {
        isAuthenticating: false,
        isAuthenticated: true,
        token: action.payload.token,
        userName: action.payload.name,
        display_name: action.payload.name,
        user: {
          ...userInitialState,
          photo: { url: action.payload.photo_url,
            thumb: { url: action.payload.photo_url }
          },
          name: action.payload.name,
          id: action.payload.id,
          organizations: [{
            ...orgs,
            roles: ["qa_student"],
            id: action.payload.organization.id,
            name: action.payload.organization.name
          }]
        }
      })
    }

    case UPDATE_ACCOUNT_REQUEST: {
      return Object.assign({}, state, {
        isUpdatingAccount: true
      })
    }

    case UPDATE_ACCOUNT_SUCCESS: {
      const newUser = Object.assign({}, state.user)
      newUser.photo.url = action.file
      newUser.about = action.about

      return Object.assign({}, state, {
        isUpdatingAccount: false,
        user: newUser
      })
    }

    case UPDATE_ACCOUNT_FAILURE: {
      return Object.assign({}, state, {
        isUpdatingAccount: false
      })
    }

    case IS_EDITING_ACCOUNT: {
      return Object.assign({}, state, {
        isEditingAccount: true
      })
    }

    case IS_NOT_EDITING_ACCOUNT: {
      return Object.assign({}, state, {
        isEditingAccount: false
      })
    }

    case RESET_PASSWORD_FAILURE: {
      return Object.assign({}, state, {
        wasPasswordReset: false
      })
    }

    case RESET_PASSWORD_SUCCESS: {
      return Object.assign({}, state, {
        wasPasswordReset: true
      })
    }

    case CLEAR_RESET_PASSWORD: {
      return Object.assign({}, state, {
        wasPasswordReset: null
      })
    }

    case USER_NEEDS_TO_RESET_PASSWORD: {
      if (action.userNeedsToResetPassword) {
        return Object.assign({}, state, {
          userNeedsToResetPassword: true
        })
      } else {
        let user = Object.assign({}, state.user)
        user.require_password_change_on_next_login = false
        return Object.assign({}, state, {
          userNeedsToResetPassword: false,
          user: user
        })
      }
    }

    case SET_CURRENT_ORGANIZATION: {
      const user = Object.assign({}, state.user)

      if (user.organizations) {
        const currentOrg = user.organizations.find(
          org => org.id === action.orgId
        )

        user.org_id = action.orgId

        if (currentOrg) {
          user.org_name = currentOrg.name
          user.organization_roles = currentOrg.roles
          user.last_skill_assessment_at = currentOrg.last_skill_assessment_at
        }

        return Object.assign({}, state, {
          user: user
        })
      } else {
        return Object.assign({}, state, {
          user: state.user
        })
      }
    }

    case SET_LAST_SKILL_ASSESSMENT_AT: {
      const user = Object.assign({}, state.user)
      user.last_skill_assessment_at = Date.now()

      return Object.assign({}, state, {
        user: user
      })
    }

    case FETCH_USER_COURSES_SUCCESS: {
      return Object.assign({}, state, {
        userHasCurrentCourses: action.user_courses.length > 0
      })
    }

    case FETCH_PAST_USER_COURSES_SUCCESS: {
      return Object.assign({}, state, {
        userHasPastCourses: action.past_user_courses.length > 0
      })
    }

    case UNLOCK_ACCOUNT_FAILURE: {
      return Object.assign({}, state, {
        isAuthenticating: false,
        isAuthenticated: false,
        isLoggedInAndRedirected: false,
        token: null,
        userName: null,
        unlockErrorStatusText: action.payload.unlockErrorStatusText
      })
    }

    case SET_THEME: {
      return {
        ...state,
        theme: action.payload
      }
    }

    default:
      return state
  }
}
